import {
  IBlogAuthorFields,
  IBlogCategoryFields,
  IBlogPostFields,
} from 'global';
import { magazineBathPath } from 'modules/magazine/constants';
import { getMagazineUrl } from 'modules/magazine/util';
import Head from 'next/head';
import Link from 'next/link';
import React from 'react';
import { jsonLdScriptProps } from 'react-schemaorg';
import { BreadcrumbList } from 'schema-dts';

interface BreadcrumbProps {
  mainCategory?: IBlogCategoryFields;
  subCategory?: IBlogCategoryFields;
  blogPost?: IBlogPostFields;
  author?: IBlogAuthorFields;
  root?: string;
}

enum Active {
  main,
  sub,
  blog,
  author,
}

type ListItemProps = {
  href: string;
  isActive?: boolean;
  text: string;
};

const BreadcrumbListItem: React.FC<ListItemProps> = ({
  href,
  isActive = false,
  text,
}) => {
  if (isActive) {
    return (
      <li className={'inline'}>
        {''} / <span className={'text-grey'}>{text}</span>
      </li>
    );
  }

  return (
    <li
      className={
        'text-primary-main/50 hover:text-primary-main/100 inline cursor-pointer'
      }
    >
      <Link href={href} passHref>
        <a>
          {' '}
          / <span className={'hover:underline'}>{text}</span>
        </a>
      </Link>
    </li>
  );
};

const Breadcrumb: React.FC<BreadcrumbProps> = ({
  mainCategory,
  subCategory,
  blogPost,
  author,
  root,
}) => {
  let activeItem;

  if (mainCategory) {
    activeItem = Active.main;
  }
  if (subCategory) {
    activeItem = Active.sub;
  }
  if (blogPost) {
    activeItem = Active.blog;
  }
  if (author) {
    activeItem = Active.author;
  }

  const generateBreadcrumbList = (
    mainCategory: IBlogCategoryFields | undefined,
    subCategory: IBlogCategoryFields | undefined,
    blogPost: IBlogPostFields | undefined,
    author: IBlogAuthorFields | undefined
  ): any => {
    const items = [
      {
        '@type': 'ListItem',
        position: 1,
        name: `Magazin`,
        item: getMagazineUrl(undefined, true),
      },
    ];
    if (mainCategory) {
      items.push({
        '@type': 'ListItem',
        position: 2,
        name: `${mainCategory.pageTitle}`,
        item: getMagazineUrl({ mainCategory }, true),
      });
    }
    if (mainCategory && subCategory) {
      items.push({
        '@type': 'ListItem',
        position: 3,
        name: `${subCategory.pageTitle}`,
        item: getMagazineUrl({ mainCategory, subCategory }, true),
      });
    }
    if (mainCategory && subCategory && blogPost) {
      items.push({
        '@type': 'ListItem',
        position: 4,
        name: `${blogPost.pageTitle}`,
        item: getMagazineUrl(
          {
            mainCategory,
            subCategory,
            blogPost,
          },
          true
        ),
      });
      if (author) {
        items.push({
          '@type': 'ListItem',
          position: 5,
          name: `${author.name}`,
          item: getMagazineUrl({ author }, true),
        });
      }
    }

    return items;
  };

  // TODO : Pt-2470 Implementing breadcrumbs schema for PD magazine
  return (
    <>
      <Head>
        <script
          {...jsonLdScriptProps<BreadcrumbList>({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: generateBreadcrumbList(
              mainCategory,
              subCategory,
              blogPost,
              author
            ),
          })}
        />
      </Head>
      <nav className={'font-black'}>
        <ol>
          {root && (
            <>
              <li
                className={
                  'text-primary-main/50 hover:text-primary-main/100 inline cursor-pointer'
                }
              >
                <Link href="/" passHref>
                  <a className={'hover:underline'}>{root}</a>
                </Link>
              </li>
              <BreadcrumbListItem
                href={`/${magazineBathPath}`}
                isActive={true}
                text="Magazin"
              />
            </>
          )}
          {!root && (
            <li
              className={
                'text-primary-main/50 hover:text-primary-main/100 inline cursor-pointer'
              }
            >
              <Link href="/magazin" passHref>
                <a className={'hover:underline'}>Magazin</a>
              </Link>
            </li>
          )}
          {/* here */}
          {mainCategory && (
            <>
              <BreadcrumbListItem
                href={`/${magazineBathPath}/${mainCategory.handle}`}
                isActive={activeItem === Active.main}
                text={mainCategory.name}
              />
              {subCategory && (
                <>
                  <BreadcrumbListItem
                    href={`/${magazineBathPath}/${mainCategory.handle}/${subCategory.handle}`}
                    isActive={activeItem === Active.sub}
                    text={subCategory.name}
                  />
                  {blogPost && (
                    <BreadcrumbListItem
                      href={`/${magazineBathPath}/${mainCategory.handle}/${subCategory.handle}/${blogPost.handle}`}
                      isActive={activeItem === Active.blog}
                      text={blogPost.title}
                    />
                  )}
                </>
              )}
            </>
          )}
          {author && (
            <BreadcrumbListItem
              href={`/${magazineBathPath}/author/${author.handle}`}
              isActive={activeItem === Active.author}
              text={author.name}
            />
          )}
        </ol>
      </nav>
    </>
  );
};

export default Breadcrumb;
